import React, { useEffect, useState } from "react";
import { createTable, deleteTable, fetchTables, updateTable } from "../services/api";
import { useT } from "../i18n/useT";

// AM/OM are machine drafts (2026-06) — pending native review
const STRINGS = {
  en: {
    heading: "Tables & Rooms",
    could_not_load: "Could not load tables.",
    could_not_create: "Could not create table.",
    confirm_delete: "Delete this table? (existing orders will lose the link)",
    new_table_number: "New table number",
    area_label: "Area / zone (optional)",
    area_placeholder: "e.g. Restaurant, Bar, Pool, Floor 1, Rooms",
    edit_area: "Edit",
    unassigned: "Unassigned",
    update_failed: "Update failed.",
    add_table: "Add table",
    table_label: "Table {n}",
    delete: "Delete",
    no_tables_yet: "No tables yet.",
    type_label: "Type",
    type_dining: "Dining",
    type_room: "Room service",
    save: "Save",
    cancel: "Cancel",
  },
  am: {
    heading: "ጠረጴዛዎች እና ክፍሎች",
    could_not_load: "ጠረጴዛዎችን መጫን አልተቻለም።",
    could_not_create: "ጠረጴዛ መፍጠር አልተቻለም።",
    confirm_delete: "ይህን ጠረጴዛ አጥፋ? (ነባር ትዕዛዞች አገናኙን ያጣሉ)",
    new_table_number: "አዲስ የጠረጴዛ ቁጥር",
    add_table: "ጠረጴዛ ጨምር",
    table_label: "ጠረጴዛ {n}",
    delete: "አጥፋ",
    no_tables_yet: "እስካሁን ጠረጴዛ የለም።",
    type_label: "አይነት",
    type_dining: "መመገቢያ",
    type_room: "የክፍል አገልግሎት",
    save: "አስቀምጥ",
    cancel: "ሰርዝ",
  },
  om: {
    heading: "Minjaalota fi Kutaalee",
    could_not_load: "Minjaalota fe'uun hin danda'amne.",
    could_not_create: "Minjaala uumuun hin danda'amne.",
    confirm_delete: "Minjaala kana balleessuu? (ajajawwan jiran hidhata ni dhabu)",
    new_table_number: "Lakkoofsa minjaala haaraa",
    add_table: "Minjaala dabali",
    table_label: "Minjaala {n}",
    delete: "Balleessi",
    no_tables_yet: "Minjaalli hin jiru.",
    type_label: "Gosa",
    type_dining: "Nyaata",
    type_room: "Tajaajila kutaa",
    save: "Olkaa'i",
    cancel: "Dhiisi",
  },
};

// Area presets always offered in the datalist, merged with any distinct
// areas already used on existing tables.
const AREA_PRESETS = ["Restaurant", "Café", "Bar", "Other", "Rooms"];

export default function ManagerTablesPage() {
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;

  const [tables, setTables] = useState([]);
  const [number, setNumber] = useState("");
  const [area, setArea] = useState("");
  const [type, setType] = useState("dining");
  const [error, setError] = useState(null);

  // Inline edit state — which card is being edited + its working values.
  const [editingId, setEditingId] = useState(null);
  const [editArea, setEditArea] = useState("");
  const [editKind, setEditKind] = useState("dining");

  const load = () =>
    fetchTables()
      .then(setTables)
      .catch((e) => setError(e?.response?.data?.detail || t("could_not_load")));

  useEffect(() => { load(); }, []);

  const areaOptions = Array.from(
    new Set([...AREA_PRESETS, ...tables.map((x) => x.area).filter(Boolean)])
  );

  const add = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await createTable(number.trim(), area.trim() || null, type);
      setNumber("");
      setArea("");
      setType("dining");
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || t("could_not_create"));
    }
  };

  const startEdit = (tb) => {
    setEditingId(tb.id);
    setEditArea(tb.area || "");
    setEditKind(tb.kind || "dining");
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async (tb) => {
    try {
      await updateTable(tb.id, { area: editArea.trim(), kind: editKind });
      setEditingId(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || t("update_failed"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete")))
      return;
    await deleteTable(id);
    load();
  };

  return (
    <>
      <h1>{t("heading")}</h1>

      {error && <div className="error">{error}</div>}

      <form className="card" onSubmit={add} style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "end", flexWrap: "wrap" }}>
        <div className="field" style={{ marginBottom: 0, flex: "0 0 200px" }}>
          <label>{t("new_table_number")}</label>
          <input
            type="text"
            maxLength={20}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="1, A1, Patio-3"
            required
          />
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "1 1 220px" }}>
          <label>{t("area_label")}</label>
          <input value={area} onChange={(e) => setArea(e.target.value)} placeholder={t("area_placeholder")} list="odra-areas" />
          <datalist id="odra-areas">
            {areaOptions.map((a) => (<option key={a} value={a} />))}
          </datalist>
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "0 0 160px" }}>
          <label>{t("type_label")}</label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="dining">{t("type_dining")}</option>
            <option value="room">{t("type_room")}</option>
          </select>
        </div>
        <button className="btn" type="submit">{t("add_table")}</button>
      </form>

      <div className="grid-cards">
        {tables.map((tb) => {
          const kind = tb.kind || "dining";
          const isEditing = editingId === tb.id;
          return (
            <div key={tb.id} className="qr-card">
              <h3>{t("table_label").replace("{n}", tb.table_number)}</h3>
              {isEditing ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <input
                    value={editArea}
                    onChange={(e) => setEditArea(e.target.value)}
                    placeholder={t("area_placeholder")}
                    list="odra-areas"
                  />
                  <select value={editKind} onChange={(e) => setEditKind(e.target.value)}>
                    <option value="dining">{t("type_dining")}</option>
                    <option value="room">{t("type_room")}</option>
                  </select>
                  <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    <button className="btn small" onClick={() => saveEdit(tb)}>
                      {t("save")}
                    </button>
                    <button className="btn secondary small" onClick={cancelEdit}>
                      {t("cancel")}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="muted" style={{ margin: "2px 0 2px" }}>{tb.area || t("unassigned")}</p>
                  <p className="muted" style={{ margin: "0 0 8px", fontSize: "0.8rem" }}>
                    {kind === "room" ? t("type_room") : t("type_dining")}
                  </p>
                  <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    <button className="btn secondary small" onClick={() => startEdit(tb)}>
                      {t("edit_area")}
                    </button>
                    <button className="btn danger small" onClick={() => remove(tb.id)}>
                      {t("delete")}
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
        {tables.length === 0 && <p className="muted">{t("no_tables_yet")}</p>}
      </div>
    </>
  );
}
